import { useEffect, useRef } from 'react';

export function BlogBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let time = 0;

    interface FloatingPaper {
      x: number;
      y: number;
      vx: number;
      vy: number;
      rotation: number;
      rotationSpeed: number;
      size: number;
      opacity: number;
    }

    interface TextLine {
      x: number;
      y: number;
      width: number;
      progress: number;
      speed: number;
    }

    let papers: FloatingPaper[] = [];
    let textLines: TextLine[] = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initElements();
    };

    const initElements = () => {
      papers = [];
      textLines = [];

      // Floating papers
      for (let i = 0; i < 6; i++) {
        papers.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: Math.random() * 0.3 + 0.1,
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.02,
          size: 40 + Math.random() * 30,
          opacity: 0.05 + Math.random() * 0.08,
        });
      }

      // Typewriter text lines
      for (let i = 0; i < 8; i++) {
        textLines.push({
          x: canvas.width * 0.7 + (Math.random() - 0.5) * 100,
          y: canvas.height * 0.2 + i * 25,
          width: 80 + Math.random() * 120,
          progress: 0,
          speed: 0.5 + Math.random() * 0.5,
        });
      }
    };

    const drawPaper = (paper: FloatingPaper) => {
      ctx.save();
      ctx.translate(paper.x, paper.y);
      ctx.rotate(paper.rotation);

      const w = paper.size;
      const h = paper.size * 1.4;

      // Paper shadow
      ctx.fillStyle = `rgba(0, 0, 0, ${paper.opacity * 0.3})`;
      ctx.fillRect(-w / 2 + 3, -h / 2 + 3, w, h);

      // Paper body
      ctx.fillStyle = `rgba(255, 255, 255, ${paper.opacity})`;
      ctx.fillRect(-w / 2, -h / 2, w, h);

      // Paper fold corner
      ctx.beginPath();
      ctx.moveTo(w / 2 - 15, -h / 2);
      ctx.lineTo(w / 2, -h / 2 + 15);
      ctx.lineTo(w / 2, -h / 2);
      ctx.closePath();
      ctx.fillStyle = `rgba(200, 200, 200, ${paper.opacity})`;
      ctx.fill();

      // Text lines on paper
      ctx.fillStyle = `rgba(168, 85, 247, ${paper.opacity * 0.5})`;
      for (let i = 0; i < 5; i++) {
        const lineY = -h / 2 + 20 + i * 12;
        const lineWidth = w * (0.5 + Math.random() * 0.4);
        ctx.fillRect(-w / 2 + 8, lineY, lineWidth, 2);
      }

      ctx.restore();
    };

    const drawFloatingPapers = () => {
      papers.forEach((paper) => {
        paper.x += paper.vx;
        paper.y += paper.vy;
        paper.rotation += paper.rotationSpeed;

        // Wrap around
        if (paper.y > canvas.height + paper.size) {
          paper.y = -paper.size;
          paper.x = Math.random() * canvas.width;
        }
        if (paper.x < -paper.size) paper.x = canvas.width + paper.size;
        if (paper.x > canvas.width + paper.size) paper.x = -paper.size;

        drawPaper(paper);
      });
    };

    const drawTypewriterEffect = () => {
      textLines.forEach((line, index) => {
        line.progress += line.speed * 0.01;
        if (line.progress > 1) line.progress = 0;

        const currentWidth = line.width * Math.min(1, line.progress * 2);

        // Line background
        ctx.fillStyle = 'rgba(168, 85, 247, 0.03)';
        ctx.fillRect(line.x, line.y, line.width, 12);

        // Typed text
        ctx.fillStyle = 'rgba(168, 85, 247, 0.1)';
        ctx.fillRect(line.x, line.y, currentWidth, 12);

        // Cursor
        if (line.progress < 0.5) {
          const cursorX = line.x + currentWidth;
          const blink = Math.sin(time * 10) > 0;
          if (blink) {
            ctx.fillStyle = 'rgba(168, 85, 247, 0.3)';
            ctx.fillRect(cursorX, line.y - 2, 2, 16);
          }
        }
      });
    };

    const drawQuoteMarks = () => {
      ctx.font = 'bold 120px Georgia';
      ctx.fillStyle = 'rgba(168, 85, 247, 0.03)';

      // Opening quote
      ctx.save();
      ctx.translate(canvas.width * 0.15, canvas.height * 0.3);
      ctx.fillText('"', 0, 0);
      ctx.restore();

      // Closing quote
      ctx.save();
      ctx.translate(canvas.width * 0.85, canvas.height * 0.7);
      ctx.fillText('"', 0, 0);
      ctx.restore();
    };

    const drawBookSpine = () => {
      const x = canvas.width * 0.05;
      const bookCount = 5;

      for (let i = 0; i < bookCount; i++) {
        const y = canvas.height * 0.6 + i * 25;
        const width = 15;
        const height = 20;

        const colors = [
          'rgba(168, 85, 247, 0.08)',
          'rgba(34, 211, 238, 0.08)',
          'rgba(59, 130, 246, 0.08)',
          'rgba(245, 158, 11, 0.08)',
          'rgba(16, 185, 129, 0.08)',
        ];

        ctx.fillStyle = colors[i];
        ctx.fillRect(x + i * 20, y, width, height);

        // Book spine detail
        ctx.strokeStyle = colors[i].replace('0.08', '0.15');
        ctx.lineWidth = 1;
        ctx.strokeRect(x + i * 20, y, width, height);
      }
    };

    const drawInkSplatter = () => {
      const splatterCount = 3;
      for (let i = 0; i < splatterCount; i++) {
        const x = canvas.width * (0.2 + i * 0.3);
        const y = canvas.height * 0.85;
        const size = 20 + Math.sin(time + i * 2) * 5;

        const gradient = ctx.createRadialGradient(x, y, 0, x, y, size);
        gradient.addColorStop(0, 'rgba(168, 85, 247, 0.08)');
        gradient.addColorStop(0.5, 'rgba(168, 85, 247, 0.04)');
        gradient.addColorStop(1, 'transparent');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();

        // Ink drops
        for (let j = 0; j < 3; j++) {
          const dropX = x + (Math.random() - 0.5) * 40;
          const dropY = y + (Math.random() - 0.5) * 40;
          const dropSize = 3 + Math.random() * 5;

          ctx.beginPath();
          ctx.arc(dropX, dropY, dropSize, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(168, 85, 247, 0.05)';
          ctx.fill();
        }
      }
    };

    const drawPenStroke = () => {
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(168, 85, 247, 0.05)';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';

      const startX = canvas.width * 0.6;
      const startY = canvas.height * 0.75;
      const progress = (time * 0.3) % 1;

      ctx.moveTo(startX, startY);

      for (let i = 0; i <= progress * 100; i++) {
        const t = i / 100;
        const x = startX + t * 200;
        const y = startY + Math.sin(t * Math.PI * 3) * 20;
        ctx.lineTo(x, y);
      }

      ctx.stroke();
    };

    const animate = () => {
      time += 0.016;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, 'rgba(168, 85, 247, 0.02)');
      gradient.addColorStop(0.5, 'transparent');
      gradient.addColorStop(1, 'rgba(34, 211, 238, 0.02)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      drawQuoteMarks();
      drawFloatingPapers();
      drawTypewriterEffect();
      drawBookSpine();
      drawInkSplatter();
      drawPenStroke();

      animationFrameId = requestAnimationFrame(animate);
    };

    resize();
    window.addEventListener('resize', resize);
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <canvas
        ref={canvasRef}
        className="w-full h-full opacity-80"
      />

      {/* Decorative elements */}
      <div className="absolute top-32 left-8 hidden lg:block">
        <div className="text-6xl text-purple-500/5 font-serif animate-pulse">A</div>
      </div>

      <div className="absolute bottom-24 right-12 hidden lg:block">
        <div className="flex items-center gap-2 opacity-30">
          <div className="w-8 h-1 bg-gradient-to-r from-purple-500/30 to-transparent rounded" />
          <div className="w-6 h-1 bg-gradient-to-r from-purple-500/20 to-transparent rounded" />
          <div className="w-4 h-1 bg-gradient-to-r from-purple-500/10 to-transparent rounded" />
        </div>
      </div>

      <div className="absolute top-1/2 right-8 hidden lg:block transform -translate-y-1/2">
        <div className="w-px h-32 bg-gradient-to-b from-transparent via-purple-500/10 to-transparent" />
      </div>
    </div>
  );
}
